import clsx from "clsx";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import { isNotNullable } from "@/utils/guards";
import Badge from "@/ui/atoms/Badge/Badge";
import Skeleton from "@/ui/atoms/Skeleton";

export type ChartOfAccountsCardProps = BoxProps & {
    isProdaStandard?: boolean;
    isDraft?: boolean;
    coaDefinition?: {
        coaId: number;
        name: string;
        type?: string;
        ownerCompanyName?: string;
        isDraft?: boolean;
        deals?: number;
        lastEditedBy?: string;
        lastEdited?: string;
    };
};

const testId = "ChartOfAccountsCard";
const ChartOfAccountsCard = (props: ChartOfAccountsCardProps) => {
    const {
        sx,
        className,
        isProdaStandard,
        isDraft,
        coaDefinition,
        ...otherProps
    } = props;

    return (
        <Box
            data-testid={testId}
            className={clsx([testId, className])}
            sx={{
                width: "100%",
                borderLeft: isNotNullable(coaDefinition)
                    ? isNotNullable(isProdaStandard) && isProdaStandard
                        ? "8px solid var(--proda-neon-blue)"
                        : isNotNullable(isDraft) && isDraft
                          ? "8px solid var(--proda-purple)"
                          : "8px solid var(--proda-green)"
                    : "8px solid var(--proda-black-10)",
                borderRadius: "4px",
                padding: "12px",
                boxShadow: "var(--shadow-default)",
                ...sx,
            }}
            {...otherProps}
        >
            {isNotNullable(coaDefinition) ? (
                <Box
                    sx={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "8px",
                            }}
                        >
                            <Box
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "16px",
                                }}
                            >
                                {coaDefinition.name}
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "8px",
                                }}
                            >
                                {coaDefinition.coaId === 1 ? (
                                    <Badge
                                        customColor={{
                                            backgroundColor:
                                                "proda-neon-blue-10",
                                            leadingIconColor: "proda-neon-blue",
                                            borderColor: "proda-neon-blue",
                                        }}
                                    >
                                        Proda Standard
                                    </Badge>
                                ) : isNotNullable(coaDefinition.isDraft) &&
                                  coaDefinition.isDraft ? (
                                    <Badge
                                        customColor={{
                                            backgroundColor: "proda-purple-10",
                                            leadingIconColor: "proda-purple",
                                            borderColor: "proda-purple",
                                        }}
                                    >
                                        Draft
                                    </Badge>
                                ) : (
                                    <Badge
                                        customColor={{
                                            backgroundColor: "proda-green-20",
                                            leadingIconColor: "proda-green",
                                            borderColor: "proda-green-120",
                                        }}
                                    >
                                        Completed
                                    </Badge>
                                )}
                                {isNotNullable(coaDefinition.type) && (
                                    <Badge severity="inactive">
                                        {coaDefinition.type}
                                    </Badge>
                                )}
                                {isNotNullable(
                                    coaDefinition.ownerCompanyName,
                                ) && (
                                    <Badge severity="inactive">
                                        {coaDefinition.ownerCompanyName}
                                    </Badge>
                                )}
                            </Box>
                        </Box>

                        {isNotNullable(coaDefinition.deals) && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    color: "var(--proda-black-30)",
                                    fontSize: "14px",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            fontWeight: 500,
                                        }}
                                    >
                                        Deals:
                                    </Box>
                                    {coaDefinition.deals}
                                </Box>
                            </Box>
                        )}

                        {(isNotNullable(coaDefinition.lastEditedBy) ||
                            isNotNullable(coaDefinition.lastEdited)) && (
                            <Box
                                sx={{
                                    fontSize: "12px",
                                    color: "var(--proda-black-30)",
                                }}
                            >
                                {isNotNullable(coaDefinition.lastEditedBy) &&
                                isNotNullable(coaDefinition.lastEdited)
                                    ? `Last Edited by: ${coaDefinition.lastEditedBy} on ${coaDefinition.lastEdited}`
                                    : isNotNullable(coaDefinition.lastEditedBy)
                                      ? `Last Edited by: ${coaDefinition.lastEditedBy}`
                                      : `Last Edited: ${coaDefinition.lastEdited}`}
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <Box sx={{ fontSize: "20px" }}>›</Box>
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            width: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                            }}
                        >
                            <Skeleton
                                variant="text"
                                sx={{
                                    width: "180px",
                                    height: "20px",
                                }}
                            />
                            <Skeleton
                                variant="rectangular"
                                sx={{
                                    width: "80px",
                                    height: "24px",
                                    borderRadius: "10px",
                                }}
                            />
                        </Box>
                        <Skeleton
                            variant="text"
                            sx={{
                                width: "120px",
                                height: "16px",
                            }}
                        />
                        <Skeleton
                            variant="text"
                            sx={{
                                width: "220px",
                                height: "14px",
                            }}
                        />
                    </Box>
                    <Skeleton
                        variant="text"
                        sx={{
                            width: "20px",
                            height: "20px",
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

export default ChartOfAccountsCard;
